import React from 'react'
import Modal from 'react-bootstrap/Modal'
import { Trans, useTranslation } from 'react-i18next'
import 'common/styling/modal-styling.scss'
import '../Modal.scss'
import './SummaryBoxModal.scss'
import { IApplicationInformation } from 'interfaces/i-application-information'
import { stringifyNum } from 'utils/commonhelpers'

interface ISummaryBox {
	applicationInformation?: IApplicationInformation
	showModal: boolean
	handleClose: any
}

export const SummaryBoxModal = ({
	applicationInformation,
	showModal,
	handleClose,
}: ISummaryBox) => {
	const { t } = useTranslation()

	const monthlyInterestRate = applicationInformation?.apr
		? (1 + applicationInformation?.apr) ** (1 / 12) - 1
		: 0
	const simpleAnnualRate = monthlyInterestRate * 12
	const compoundAnnualRate = (1 + monthlyInterestRate) ** 12 - 1

	return (
		<Modal
			show={showModal}
			onHide={handleClose}
			animation={false}
			centered
			className="summary-box-modal"
			backdrop="static">
			<Modal.Header closeButton>
				<h3> {t('SummaryBoxHeader')}</h3>
			</Modal.Header>
			<Modal.Body>
				<div className="table-wrap">
					<table className="summary-box-table">
						<tbody>
							<tr>
								<td>{t('APR')}</td>
								<td>
									{stringifyNum(applicationInformation?.apr, {
										style: 'percent',
									})}{' '}
									{t('APR')}
								</td>
							</tr>
							<tr>
								<td>{t('InterestRatesHeader')}</td>
								<td>
									<table className="interest-rates-table">
										<thead>
											<tr>
												<th></th>
												<th>
													{t('MonthlyRateHeader')}
												</th>
												<th>
													{t(
														'SimpleAnnualRateHeader',
													)}
												</th>
												<th>
													{t(
														'CompoundedAnnualRateHeader',
													)}
												</th>
											</tr>
										</thead>
										<tbody>
											<tr>
												<td>{t('PurchasesHeader')}</td>
												<td
													data-label={t(
														'MonthlyRateHeader',
													)}>
													{stringifyNum(
														monthlyInterestRate,
														{
															style: 'percent',
														},
													)}
												</td>
												{/* for following values will need calculations to be provided */}
												<td
													data-label={t(
														'SimpleAnnualRateHeader',
													)}>
													{stringifyNum(
														simpleAnnualRate,
														{
															style: 'percent',
														},
													)}
												</td>
												<td
													data-label={t(
														'CompoundedAnnualRateHeader',
													)}>
													{stringifyNum(
														compoundAnnualRate,
														{
															style: 'percent',
														},
													)}
												</td>
											</tr>
											<tr>
												<td>
													{t(
														'CashTransactionsHeader',
													)}
												</td>
												<td
													data-label={t(
														'MonthlyRateHeader',
													)}>
													{stringifyNum(
														monthlyInterestRate,
														{
															style: 'percent',
														},
													)}
												</td>
												{/* for following values will need calculations to be provided */}
												<td
													data-label={t(
														'SimpleAnnualRateHeader',
													)}>
													{stringifyNum(
														simpleAnnualRate,
														{
															style: 'percent',
														},
													)}
												</td>
												<td
													data-label={t(
														'CompoundedAnnualRateHeader',
													)}>
													{stringifyNum(
														compoundAnnualRate,
														{
															style: 'percent',
														},
													)}
												</td>
											</tr>
										</tbody>
									</table>
								</td>
							</tr>
							<tr>
								<td>{t('InterestFreePeriodHeader')}</td>
								<td>
									<Trans
										t={t}
										i18nKey="InterestFreePeriodValue">
										<ul>
											<li>
												You will not pay interest on
												purchases if you pay your full
												statement balance by the payment
												due date shown on your statement
												each month.
											</li>
											<li>
												The maximum interest-free period
												on purchases is up to 56 days,
												depending on when the purchase
												was made during the statement
												period.
											</li>
											<li>
												There is no interest-free period
												for cash transactions, fees or
												interest.
											</li>
										</ul>
									</Trans>
								</td>
							</tr>
							<tr>
								<td>{t('InterestChargingHeader')}</td>
								<td>{t('InterestChargingValue')}</td>
							</tr>
							<tr>
								<td>{t('MinimumPaymentHeader')}</td>
								<td>
									<Trans t={t} i18nKey="MinimumPaymentValue">
										Each month you must pay at least the
										minimum payment shown on your statement
										by the payment due date. Your minimum
										payment will be the greater of:-
										<ul>
											<li>
												£5 or the full balance if lower,
											</li>
										</ul>
										OR
										<ul>
											<li>
												An amount equal to any interest,
												Late Payment Fees (if
												applicable), plus X% of your
												monthly statement balance.
											</li>
										</ul>
										<p>
											<strong>
												Paying only the minimum payment
												each month will increase the
												total amount of interest you pay
												and the time it takes to repay
												your balance.
											</strong>
										</p>
									</Trans>
								</td>
							</tr>
							<tr>
								<td>{t('PaymentsAllocationHeader')}</td>
								<td>{t('PaymentsAllocationValue')}</td>
							</tr>
							<tr>
								<td>{t('FeesHeader')}</td>
								<td>{t('FeesValue')}</td>
							</tr>
							<tr>
								<td>{t('ChargesHeader')}</td>
								<td>{t('ChargesValue')}</td>
							</tr>
							<tr>
								<td>{t('UsingCardAbroadHeader')}</td>
								<td>
									<Trans t={t} i18nKey="UsingCardAbroadValue">
										<ul>
											<li>
												<strong>
													Payment Scheme Exchange Rate
												</strong>
												- Mastercard rates can be found
												at:
												mastercard.co.uk/en-gb/personal/get-support/convert-currency.html
											</li>
											<li>
												We do not add any additional
												fees or charges to your foreign
												transactions, but the cash fees
												above continue to apply.
											</li>
										</ul>
									</Trans>
								</td>
							</tr>
							<tr>
								<td>{t('LatePaymenetFeeHeader')}</td>
								<td>{t('LatePaymenetFeeValue')}</td>
							</tr>
						</tbody>
					</table>
				</div>
			</Modal.Body>
			<Modal.Footer></Modal.Footer>
		</Modal>
	)
}
